/**
 * Service for handling Swedish tax table calculations from optimized JSON files.
 * Loads year-specific tax tables from /public/tax-tables-{year}.json.
 */

import { TaxTableEntry } from '@/types'

/**
 * Optimized tax table structure
 */
interface OptimizedTaxTable {
  brackets: number[][] // [from, to, col1, col2, col3, col4, col5, col6, isPercentage]
}

interface OptimizedTaxTables {
  [tableNumber: string]: OptimizedTaxTable
}

/**
 * Enhanced tax table entry with percentage flag
 */
export interface EnhancedTaxTableEntry extends TaxTableEntry {
  isPercentage: boolean // true if "30%", false if "30B" (absolute amounts)
}

export class TaxTableService {
  // Cached parsed tax tables — key: "{year}:{tableNumber}" e.g. "2026:31"
  private static tables: Map<string, EnhancedTaxTableEntry[]> = new Map()

  // Cached raw JSON data per year
  private static allDataByYear: Map<number, OptimizedTaxTables> = new Map()

  /**
   * Derive the tax year from a period string and optional payout date.
   *
   * @param period    Period string "yyyy-mm-01"
   * @param payoutDate Optional payout date — if set, its year takes precedence
   */
  static getTaxYear(period: string, payoutDate?: Date): number {
    if (payoutDate) return payoutDate.getFullYear()
    return parseInt(period.substring(0, 4), 10)
  }

  /**
   * Load the JSON data for a given year (cached after first load).
   */
  private static async loadData(year: number): Promise<OptimizedTaxTables> {
    if (this.allDataByYear.has(year)) {
      return this.allDataByYear.get(year)!
    }

    try {
      const response = await fetch(`/tax-tables-${year}.json`)
      if (!response.ok) {
        throw new Error(`Skattetabeller för ${year} saknas (${response.statusText})`)
      }

      const data: OptimizedTaxTables = await response.json()
      this.allDataByYear.set(year, data)
      console.log(`✓ Skattetabeller ${year} laddade: ${Object.keys(data).length} tabeller`)
      return data
    } catch (error) {
      console.error(`Fel vid läsning av skattetabeller för ${year}:`, error)
      throw new Error(
        `Skattetabeller för ${year} saknas: ${error instanceof Error ? error.message : 'Okänt fel'}`
      )
    }
  }

  /**
   * Load a specific tax table for a given year into memory.
   */
  static async loadTaxTable(tableNumber: number, year: number): Promise<void> {
    const cacheKey = `${year}:${tableNumber}`
    if (this.tables.has(cacheKey)) return

    const allData = await this.loadData(year)
    const tableData = allData[tableNumber.toString()]

    if (!tableData) {
      throw new Error(`Skattetabell ${tableNumber} hittades inte för år ${year}`)
    }

    // Convert compact bracket array to internal format
    // Format: [from, to, col1, col2, col3, col4, col5, col6, isPercentage]
    const entries: EnhancedTaxTableEntry[] = tableData.brackets.map(bracket => ({
      dayCount: bracket[8] === 1 ? '30%' : '30B',
      tableNumber,
      incomeFrom: bracket[0],
      incomeTo: bracket[1],
      column1: bracket[2],
      column2: bracket[3],
      column3: bracket[4],
      column4: bracket[5],
      column5: bracket[6],
      column6: bracket[7],
      isPercentage: bracket[8] === 1
    }))

    this.tables.set(cacheKey, entries)
    console.log(`✓ Skattetabell ${tableNumber} (${year}) laddad med ${entries.length} intervall`)
  }

  /**
   * Get tax amount for a given gross salary using the specified table, column, and year.
   */
  static getTaxAmount(
    grossSalary: number,
    tableNumber: number,
    taxColumn: number,
    year: number
  ): number {
    const cacheKey = `${year}:${tableNumber}`
    const entries = this.tables.get(cacheKey)

    if (!entries || entries.length === 0) {
      throw new Error(`Tax table ${tableNumber} not loaded`)
    }

    if (taxColumn < 1 || taxColumn > 6) {
      throw new Error(`Invalid tax column: ${taxColumn}. Must be between 1 and 6`)
    }

    // Round to whole kronor to avoid floating-point gaps between brackets
    const roundedSalary = Math.round(grossSalary)

    // Find the matching bracket
    for (const entry of entries) {
      if (roundedSalary >= entry.incomeFrom && roundedSalary <= entry.incomeTo) {
        const columnKey = `column${taxColumn}` as keyof TaxTableEntry
        const taxValue = entry[columnKey] as number
        return entry.isPercentage
          ? Math.round(grossSalary * (taxValue / 100))
          : taxValue
      }
    }

    // Below first bracket → 0
    if (entries.length > 0 && roundedSalary < entries[0].incomeFrom) {
      return 0
    }

    // Above highest bracket → use last bracket
    if (entries.length > 0 && roundedSalary > entries[entries.length - 1].incomeTo) {
      const lastEntry = entries[entries.length - 1]
      const columnKey = `column${taxColumn}` as keyof TaxTableEntry
      const taxValue = lastEntry[columnKey] as number
      return lastEntry.isPercentage
        ? Math.round(grossSalary * (taxValue / 100))
        : taxValue
    }

    // Salary falls in a gap between brackets — the table is incomplete.
    // Throwing prevents silently reporting 0 tax (see 2026 table data issue).
    throw new Error(
      `Skattetabell ${tableNumber} (${year}) saknar intervall för bruttolön ${roundedSalary} kr. Skattetabellens data är ofullständig.`
    )
  }

  /**
   * Get available tax table numbers for a given year.
   */
  static async getAvailableTables(year: number = new Date().getFullYear()): Promise<number[]> {
    try {
      const allData = await this.loadData(year)
      return Object.keys(allData).map(Number).sort((a, b) => a - b)
    } catch (error) {
      console.error('Error getting available tables:', error)
      return []
    }
  }

  /**
   * Check if a tax table for a given year is already loaded.
   */
  static isTableLoaded(tableNumber: number, year: number): boolean {
    return this.tables.has(`${year}:${tableNumber}`)
  }
}
